import React, { useState, useEffect } from 'react';
import { Heart, Plus, Calendar, Compass, Settings } from 'lucide-react';
import './index.css';
import { DatePlan, DatePlannerState, PreviousDate } from './types';
import { StorageService } from './services/storage';
import { DateGenerator } from './services/dateGenerator';
import Home from './screens/Home';
import Planner from './screens/Planner';
import Results from './screens/Results';
import Itinerary from './screens/Itinerary';
import History from './screens/History';
import Saved from './screens/Saved';

type Screen = 'home' | 'planner' | 'generating' | 'results' | 'itinerary' | 'history' | 'saved' | 'settings';

export default function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('home');
  const [plans, setPlans] = useState<DatePlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<DatePlan | null>(null);
  const [previousDates, setPreviousDates] = useState<PreviousDate[]>([]);
  const [darkMode, setDarkMode] = useState(false);

  const initialState: DatePlannerState = {
    startLocation: 'Jadavpur',
    endLocation: 'Jadavpur',
    date: new Date().toISOString().split('T')[0],
    startTime: '12:00',
    duration: 300, // 5 hours
    budget: 1000,
    transportModes: ['metro', 'local_train', 'bus', 'walking'],
    vibes: [],
    excludedPlaces: StorageService.getExcludedPlaces(),
    maxTravelTime: 20,
    walkingTolerance: 'medium',
    foodPreferences: [],
    indoorOutdoor: 'any',
    numPeople: 2,
  };

  const [plannerState, setPlannerState] = useState<DatePlannerState>(initialState);

  useEffect(() => {
    loadPreviousDates();
  }, []);

  const loadPreviousDates = () => {
    const dates = StorageService.getPreviousDates();
    setPreviousDates(dates);
  };

  const handleGeneratePlans = (state: DatePlannerState) => {
    setPlannerState(state);
    setCurrentScreen('generating');
    
    // Simulate API call
    setTimeout(() => {
      const generator = new DateGenerator(state, state.excludedPlaces);
      const generatedPlans = generator.generateDatePlans();
      setPlans(generatedPlans);
      setCurrentScreen('results');
    }, 1500);
  };

  const handleSelectPlan = (plan: DatePlan) => {
    setSelectedPlan(plan);
    setCurrentScreen('itinerary');
  };

  const handleSavePlan = (plan: DatePlan) => {
    StorageService.savePlan(plan);
    // Show toast
    alert('Date plan saved! ❤️');
  };

  const handleCompleteDatePlan = (plan: DatePlan) => {
    const newDate: PreviousDate = {
      id: `date-${Date.now()}`,
      date: plan.date,
      title: plan.title,
      stops: plan.stops.map(s => s.place.name),
      createdAt: new Date().toISOString(),
      completed: true,
    };
    StorageService.addPreviousDate(newDate);
    loadPreviousDates();
    setCurrentScreen('home');
    alert('Great! We\'ll avoid repeating these places next time. ❤️');
  };

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    if (!darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-slate-900' : 'bg-slate-50'}`}>
      {currentScreen === 'home' && (
        <Home onStartPlanning={() => setCurrentScreen('planner')} />
      )}

      {currentScreen === 'planner' && (
        <Planner onGenerate={handleGeneratePlans} initialState={plannerState} />
      )}

      {currentScreen === 'generating' && (
        <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-rose-50 to-pink-50">
          <div className="text-center">
            <div className="inline-block mb-6">
              <div className="w-16 h-16 border-4 border-rose-200 border-t-rose-600 rounded-full animate-spin"></div>
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Building Your Date</h2>
            <p className="text-slate-600">Creating perfect moments for you both...</p>
          </div>
        </div>
      )}

      {currentScreen === 'results' && (
        <Results 
          plans={plans} 
          onSelectPlan={handleSelectPlan}
          onSavePlan={handleSavePlan}
          onBackHome={() => setCurrentScreen('home')}
        />
      )}

      {currentScreen === 'itinerary' && selectedPlan && (
        <Itinerary 
          plan={selectedPlan}
          onBack={() => setCurrentScreen('results')}
          onSave={handleSavePlan}
          onComplete={() => handleCompleteDatePlan(selectedPlan)}
        />
      )}

      {currentScreen === 'history' && (
        <History 
          dates={previousDates}
          onBack={() => setCurrentScreen('home')}
        />
      )}

      {currentScreen === 'saved' && (
        <Saved onBack={() => setCurrentScreen('home')} />
      )}

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40">
        <div className="flex justify-around items-center h-16 max-w-md mx-auto w-full">
          <button
            onClick={() => setCurrentScreen('home')}
            className={`flex flex-col items-center gap-1 py-2 px-4 transition-colors ${
              currentScreen === 'home' ? 'text-rose-600' : 'text-slate-600'
            }`}
          >
            <Heart size={24} />
            <span className="text-xs">Home</span>
          </button>
          <button
            onClick={() => setCurrentScreen('planner')}
            className={`flex flex-col items-center gap-1 py-2 px-4 transition-colors ${
              currentScreen === 'planner' || currentScreen === 'results' ? 'text-rose-600' : 'text-slate-600'
            }`}
          >
            <Plus size={24} />
            <span className="text-xs">Plan</span>
          </button>
          <button
            onClick={() => setCurrentScreen('history')}
            className={`flex flex-col items-center gap-1 py-2 px-4 transition-colors ${
              currentScreen === 'history' ? 'text-rose-600' : 'text-slate-600'
            }`}
          >
            <Calendar size={24} />
            <span className="text-xs">History</span>
          </button>
          <button
            onClick={() => setCurrentScreen('saved')}
            className={`flex flex-col items-center gap-1 py-2 px-4 transition-colors ${
              currentScreen === 'saved' ? 'text-rose-600' : 'text-slate-600'
            }`}
          >
            <Compass size={24} />
            <span className="text-xs">Saved</span>
          </button>
        </div>
      </nav>

      {/* App is mobile-first, bottom nav takes space */}
      <div className="h-16"></div>
    </div>
  );
}
